import React, { MutableRefObject, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import CustomInput, {
  CustomInputLabel,
} from "src/app/components/CustomInput/CustomInput";
import { CustomTokenInput } from "src/app/components/CustomTokenInput/CustomTokenInput";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { ModalColumn } from "src/app/components/Modal/Modal";
import { MaterialTooltip } from "src/app/components/MaterialTooltip/MaterialTooltip";
import { SelectTags } from "src/app/components/SelectTags/SelectTags";
import { translateMessage } from "src/app/methods/translateMessage";
import { RootState } from "src/redux/reducers";
import { Pane } from "src/app/components/Pane/Pane";
import {
  PaneFooter,
  PaneTopbar,
  PaneTopbarLeft,
  PaneTopbarToolbar,
} from "src/app/components/Pane/PaneTopbar/PaneTopbar";
import { Button } from "src/app/components/Button/Button";
import { SaveDashboardModal } from "../../components/SocialInsightsDashboardManagement/modals/SaveDashboardModal/SaveDashboardModal";
import { PillSelector } from "src/app/components/PillSelector/PillSelector";
import {
  CriteriaOption,
  InsightsDashboardSetup,
  WorkspacePublicationsType,
} from "src/redux/insights-dashboard/types";
import { ReactComponent as AtLeastOneIcon } from "src/images/at-least-one.svg";
import { ReactComponent as AllSelectedIcon } from "src/images/all-selected.svg";
import { ReactComponent as EraserIcon } from "src/images/eraser.svg";
import SocialPlatformTags from "src/app/pages/CreatorDiscoveryTool/legacy/app/audiences-manager/AudiencesSearch/components/filters/SocialPlatform/SocialPlatformTags";
import CriteriaOptionSelector from "src/app/pages/CreatorDiscoveryTool/legacy/app/audiences-manager/AudiencesSearch/components/filters/CriteriaOptionSelector/CriteriaOptionSelector";
import { ReactComponent as FilterIcon } from "src/images/filter.svg";
import { IDDateSelector } from "../../components/IDDateSelector/IDDateSelector";
import { IDDateRangeOption } from "../../InsightsDashboard";
import {
  getInsightsDashboardDefaultDates,
  mapInsightsDashboardDateRangeToRequest,
  mapInsightsDashboardDatesToRequest,
  mapInsightsDashboardSetupToRequest,
} from "../../utils";
import { useHistory, useLocation } from "react-router";
import { API_URLS } from "src/utils/API_URLS";
import axios from "axios";
import {
  getDashboardList,
  setIDDates,
  setSelectedDashboard,
  setSetup,
} from "src/redux";
import { showToast } from "src/app/methods/showToast";
import { customEqual, showErrorToast } from "src/utils/methods";
import CONSTS from "src/app/pages/CreatorDiscoveryTool/legacy/app/helpers/consts";
import sortKeysDeep from "src/utils/sortKeysDeep";

import "./InsightsDashboardSetupPane.scss";

export enum SetupModes {
  Create = "create",
  Edit = "edit",
}

export const SETUP_MODE_QUERY_PARAM_KEY = "setupMode";

interface Props {
  loading: boolean;
  showPane: boolean;
  blockDebounce: MutableRefObject<boolean>;
  setupMode: SetupModes | undefined;
}

export interface IDCountry {
  value: string;
  label: string;
  locale: string;
}

export const initialIDPlatforms = [
  "instagram",
  "tik_tok",
  "youtube",
  "snapchat",
];

const initialLocalSetup: InsightsDashboardSetup = {
  countries: [],
  platforms: [],
  hashtagsCriteria: { value: "or", label: "Or" },
  mentionsCriteria: { value: "or", label: "Or" },
  hashtagsMentionsCriteria: { value: "or", label: "Or" },
  hashtags: [],
  mentions: [],
  workspacePublicationsType: WorkspacePublicationsType.All,
};

const areSetupsTheSame = (
  firstSetup: InsightsDashboardSetup,
  secondSetup: InsightsDashboardSetup,
) => {
  const firstSetupSorted = sortKeysDeep(firstSetup);
  const secondSetupSorted = sortKeysDeep(secondSetup);

  return customEqual(firstSetupSorted, secondSetupSorted);
};

const InsightsDashboardSetupPane = (props: Props) => {
  const { loading, showPane, blockDebounce, setupMode } = props;

  const {
    setupConfig,
    setupConfigLoading,
    setup,
    showPublicationsFilter,
    selectedDashboard,
    dateRange,
    dateFrom,
    dateTo,
    dateRanges,
  } = useSelector((state: RootState) => state.insightsDashboardReducer);

  const { activeWorkspaceUuid } = useSelector(
    (state: RootState) => state.mainReducer,
  );

  const [countriesFilter, setCountriesFilter] = useState("");
  const [countriesData, setCountriesData] = useState<IDCountry[]>([]);
  const [localSetup, setLocalSetup] = useState<InsightsDashboardSetup>(
    setupMode === SetupModes.Edit
      ? { ...initialLocalSetup, ...setup }
      : initialLocalSetup,
  );
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);

  const [localDateFrom, setLocalDateFrom] = useState<string | null>(
    dateFrom ?? null,
  );
  const [localDateTo, setLocalDateTo] = useState<string | null>(dateTo ?? null);
  const [localDateRange, setLocalDateRange] =
    useState<IDDateRangeOption | null>(dateRange ?? null);
  const [customDateError, setCustomDateError] = useState(false);

  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const { pathname } = useLocation();

  const {
    countries,
    platforms,
    hashtagsCriteria,
    mentionsCriteria,
    hashtagsMentionsCriteria,
    hashtags,
    mentions,
    workspacePublicationsType,
  } = localSetup || {};

  const setupBaseline =
    setupMode === SetupModes.Edit ? setup : initialLocalSetup;
  const isSetupMatchSavedState = areSetupsTheSame(localSetup, setupBaseline);

  const dateBaseline =
    setupMode === SetupModes.Edit
      ? {
          dateFrom: dateFrom ?? null,
          dateTo: dateTo ?? null,
          dateRange: dateRange ?? null,
        }
      : getInsightsDashboardDefaultDates();
  const isDateMatchSavedState =
    localDateFrom === dateBaseline.dateFrom &&
    localDateTo === dateBaseline.dateTo &&
    customEqual(localDateRange, dateBaseline.dateRange);

  const disableBackdropClick =
    !isSetupMatchSavedState || !isDateMatchSavedState;

  const setLocalSetupValue = (
    key: keyof InsightsDashboardSetup,
    value: any,
  ) => {
    setLocalSetup({
      ...localSetup,
      [key]: value,
    });
  };

  useEffect(() => {
    let newCountriesData: IDCountry[] = [];
    if (
      localSetup.workspacePublicationsType ===
      WorkspacePublicationsType.WorkspaceOnly
    ) {
      newCountriesData = setupConfig?.wsCountries || [];
    } else {
      newCountriesData = setupConfig?.countries || [];
    }

    setLocalSetup((prev) => {
      const filteredCountries = prev.countries.filter((country) =>
        newCountriesData.find((item) => item.value === country.value),
      );

      return {
        ...prev,
        countries: filteredCountries,
      };
    });
    setCountriesData(newCountriesData);
  }, [
    localSetup.workspacePublicationsType,
    setupConfig?.countries,
    setupConfig?.wsCountries,
  ]);

  const hidePane = () => {
    history.push({ pathname });
  };

  const clear = () => {
    setLocalSetup(initialLocalSetup);

    const defaultDates = getInsightsDashboardDefaultDates();
    setLocalDateFrom(defaultDates.dateFrom);
    setLocalDateTo(defaultDates.dateTo);
    setLocalDateRange(defaultDates.dateRange);
    setCustomDateError(false);
  };

  const countryClick = (country: IDCountry) => {
    if (countries.find((item) => item.value === country.value)) {
      setLocalSetupValue(
        "countries",
        countries.filter((item) => item.value !== country.value),
      );
    } else {
      const foundCountry = countriesData?.find(
        (item) => item.value === country.value,
      );

      if (foundCountry) {
        setLocalSetupValue("countries", [...countries, country]);
      }
    }
  };

  const normalizeTokens = (tokens: string[]) => {
    const normalized = tokens
      .flatMap((token) => token.split(/[\s,]+/g))
      .map((token) => token.trim().replace(/^[@#]+/, ""))
      .filter(Boolean);

    return [...new Set(normalized)];
  };

  const setHashtags = (newValue: string[]) => {
    setLocalSetupValue("hashtags", normalizeTokens(newValue));
  };

  const hashtagsMentionsCriteriaEnabled =
    hashtags.length > 0 && mentions.length > 0;

  const setMentions = (newValue: string[]) => {
    setLocalSetupValue("mentions", normalizeTokens(newValue));
  };

  const isMarketViewMissingHashtagOrMention =
    workspacePublicationsType === WorkspacePublicationsType.All &&
    hashtags.length === 0 &&
    mentions.length === 0;

  const handleSubmit = () => {
    if (isMarketViewMissingHashtagOrMention) {
      showErrorToast({
        id: "ws_market_view_requires_hashtag_or_mention",
        defaultMessage: "Market view requires at least one hashtag or mention",
      });

      return;
    }

    if (
      localDateRange?.value === "custom" &&
      (!localDateFrom || !localDateTo)
    ) {
      setCustomDateError(true);

      return;
    }

    blockDebounce.current = true;
    dispatch(setSetup(localSetup));
    dispatch(
      setIDDates({
        dateFrom: localDateFrom,
        dateTo: localDateTo,
        dateRange: localDateRange,
      }),
    );

    if (setupMode === SetupModes.Create) {
      setShowSaveModal(true);
    } else {
      hidePane();
      updateDashboard();
    }
  };

  const updateDashboard = async () => {
    try {
      setDashboardLoading(true);

      // showTrends is a local-only UI preference and must never be sent to the backend.
      const { showTrends: _showTrends, ...filtersWithoutShowTrends } =
        selectedDashboard?.filters || {};

      const updatedDashboard = {
        ...selectedDashboard,
        filters: {
          ...filtersWithoutShowTrends,
          ...mapInsightsDashboardSetupToRequest(localSetup),
          dateRange: mapInsightsDashboardDateRangeToRequest(localDateRange),
          ...mapInsightsDashboardDatesToRequest(
            localDateRange,
            localDateFrom,
            localDateTo,
          ),
        },
      };

      await axios.post(API_URLS.editInsightsDashboard, {
        ...updatedDashboard,
        wsWorkspaceUuid: activeWorkspaceUuid,
      });

      dispatch(setSelectedDashboard(updatedDashboard));
      dispatch(getDashboardList(activeWorkspaceUuid));
      showToast(
        "info",
        <IDHFormattedMessage id="ws_info" defaultMessage="Info" />,
        <IDHFormattedMessage
          id="ws_dashboard_updated_successfully"
          defaultMessage="Dashboard updated successfully"
        />,
      );
      hidePane();
    } catch (err) {
      showErrorToast();
    } finally {
      setDashboardLoading(false);
    }
  };

  const countryOptions = useMemo(
    () =>
      countriesData?.filter((country) =>
        country.label.toLowerCase().includes(countriesFilter.toLowerCase()),
      ) || [],
    [countriesData, countriesFilter],
  );

  return (
    <Pane
      className="id-setup-pane"
      backdropClassName="id-setup-pane__backdrop"
      showPane={showPane}
      hidePane={hidePane}
      width={500}
      disableBackdropClick={disableBackdropClick}
    >
      <PaneTopbar>
        <PaneTopbarLeft>
          <FilterIcon className="filters-header__icon" />
          <span className="filters-header__title">
            {setupMode === SetupModes.Create ? (
              <IDHFormattedMessage id="ws_creating" defaultMessage="Creating" />
            ) : (
              <IDHFormattedMessage id="ws_filters" defaultMessage="Filters" />
            )}
          </span>
        </PaneTopbarLeft>
        <PaneTopbarToolbar hidePane={hidePane} />
      </PaneTopbar>
      <div className="id-setup-pane__content">
        <ModalColumn className="hashtag_column" data-qa="hashtags">
          <CustomInputLabel>
            <IDHFormattedMessage id="ws_hashtags" defaultMessage="Hashtags" />
          </CustomInputLabel>

          <div className="token-input-wrapper">
            <CustomTokenInput
              value={[]}
              onChange={(newValue: string[]) =>
                setHashtags([...hashtags, ...newValue])
              }
              placeholder={translateMessage({
                intl,
                id: "ws_enter_hashtags_to_detect",
                defaultMessage: "Enter hashtags to detect",
              })}
              separators={[",", " "]}
              forbiddenChars={["#", "@"]}
            />
            <CriteriaOptionSelector
              value={hashtagsCriteria?.value}
              setValue={(newValue: CriteriaOption) =>
                setLocalSetupValue("hashtagsCriteria", newValue)
              }
            />
          </div>

          <SelectTags
            // @ts-ignore
            feed={[]}
            // @ts-ignore
            values={hashtags}
            // @ts-ignore
            getCustomLabel={(v) => `#${v}`}
            setValue={() => {}}
            unsetValue={(v: string) =>
              setHashtags(hashtags.filter((item) => item !== v))
            }
            clearValues={() => setLocalSetupValue("hashtags", [])}
            isExpandable
          />
        </ModalColumn>

        {hashtagsMentionsCriteriaEnabled && (
          <div className="id-setup-pane__hashtags-mentions-criteria-row">
            <span className="id-setup-pane__hashtags-mentions-criteria-label">
              <IDHFormattedMessage
                id="ws_between_hashtags_and_mentions"
                defaultMessage="between Hashtags and Mentions"
              />
            </span>
            <div className="id-setup-pane__hashtags-mentions-criteria-selector">
              <CriteriaOptionSelector
                value={hashtagsMentionsCriteria?.value}
                setValue={(newValue: CriteriaOption) => {
                  setLocalSetupValue("hashtagsMentionsCriteria", newValue);
                }}
                customOptions={[
                  {
                    value: "or",
                    icon: <AtLeastOneIcon />,
                    header: (
                      <IDHFormattedMessage
                        id="ws_either_section_header"
                        defaultMessage="Either section"
                      />
                    ),
                    text: (
                      <IDHFormattedMessage
                        id="ws_either_section_text"
                        defaultMessage="Results must match the criteria defined in either Hashtags or Mentions. They may also match both sections. This will return more results."
                      />
                    ),
                  },
                  {
                    value: "and",
                    icon: <AllSelectedIcon />,
                    header: (
                      <IDHFormattedMessage
                        id="ws_both_sections_header"
                        defaultMessage="Both sections"
                      />
                    ),
                    text: (
                      <IDHFormattedMessage
                        id="ws_both_sections_header_text"
                        defaultMessage="Results must match the criteria defined in both Hashtags and Mentions. This will return fewer, more relevant results."
                      />
                    ),
                  },
                ]}
              />
            </div>
          </div>
        )}

        <ModalColumn className="mention_column" data-qa="mentions">
          <CustomInputLabel>
            <IDHFormattedMessage id="ws_mentions" defaultMessage="Mentions" />
          </CustomInputLabel>

          <div className="token-input-wrapper">
            <CustomTokenInput
              value={[]}
              onChange={(newValue: string[]) =>
                setMentions([...mentions, ...newValue])
              }
              placeholder={translateMessage({
                intl,
                id: "ws_enter_mentions_to_detect",
                defaultMessage: "Enter mentions to detect",
              })}
              separators={[",", " "]}
              forbiddenChars={["#", "@"]}
            />
            <CriteriaOptionSelector
              value={mentionsCriteria?.value}
              setValue={(newValue: CriteriaOption) =>
                setLocalSetupValue("mentionsCriteria", newValue)
              }
            />
          </div>

          <SelectTags
            // @ts-ignore
            feed={[]}
            // @ts-ignore
            values={mentions}
            // @ts-ignore
            getCustomLabel={(v) => `@${v}`}
            setValue={() => {}}
            unsetValue={(v: string) =>
              setMentions(mentions.filter((item) => item !== v))
            }
            clearValues={() => setLocalSetupValue("mentions", [])}
            isExpandable
          />
        </ModalColumn>

        <ModalColumn data-qa="platforms">
          <SocialPlatformTags
            selectedPlatformValues={platforms}
            setSelectedPlatformValues={(v: string) =>
              setLocalSetupValue("platforms", v)
            }
            isExpandable={false}
            // @ts-ignore
            hiddenPlatforms={[CONSTS.socialProvider.facebook]}
          >
            <div />
          </SocialPlatformTags>
        </ModalColumn>

        <ModalColumn data-qa="countries">
          <CustomInputLabel>
            <IDHFormattedMessage id="ws_countries" defaultMessage="Countries" />
          </CustomInputLabel>

          <CustomInput
            value={countriesFilter}
            onChange={(e) => setCountriesFilter(e.target.value)}
            placeholder={intl.formatMessage({
              id: "ws_enter_countries",
              defaultMessage: "Enter countries",
            })}
          />

          <SelectTags
            key={setup?.countries?.length}
            // @ts-ignore
            feed={countryOptions}
            // @ts-ignore
            values={countries}
            // @ts-ignore
            getCustomLabel={(item) => item.label}
            setValue={countryClick}
            unsetValue={countryClick}
            clearValues={() => setLocalSetupValue("countries", [])}
            isExpandable
          />
        </ModalColumn>

        <ModalColumn data-qa="time">
          <IDDateSelector
            items={dateRanges || []}
            onChange={(newValue) => {
              setLocalDateRange(newValue);
              setCustomDateError(false);
            }}
            value={localDateRange}
            dateFrom={localDateFrom}
            setDateFrom={setLocalDateFrom}
            dateTo={localDateTo}
            setDateTo={setLocalDateTo}
            onCustomRangeChange={(newDateFrom, newDateTo) => {
              setLocalDateFrom(newDateFrom);
              setLocalDateTo(newDateTo);
              setLocalDateRange({ value: "custom", label: "Custom" });
              setCustomDateError(false);
            }}
            customDateError={customDateError}
            customDateErrorMessage={
              <IDHFormattedMessage
                id="ws_select_date_range"
                defaultMessage="Select a date range"
              />
            }
          />
        </ModalColumn>

        {showPublicationsFilter && (
          <ModalColumn
            className="publication-type_column"
            data-qa="publicationType"
          >
            <CustomInputLabel className="idh-label--with-tooltip">
              <IDHFormattedMessage
                id="ws_insight_scope"
                defaultMessage="Insight scope"
              />
              <MaterialTooltip
                id="ws_insight_scope_tooltip"
                content={
                  <>
                    <ul>
                      <li>
                        <strong>
                          <IDHFormattedMessage
                            id="insight_scope_market_view"
                            defaultMessage="Market view"
                          />
                          :
                        </strong>{" "}
                        <IDHFormattedMessage
                          id="ws_insight_scope_tooltip_market_view_text"
                          defaultMessage="Uses eligible social posts, metrics and activities registered by the platform across creators, brands, countries and social platforms. This is not internet-wide monitoring and may include matching activity from your workspace. Use it to discover broader trends, benchmark categories or monitor competitors."
                        />
                      </li>
                      <li>
                        <br />
                        <strong>
                          <IDHFormattedMessage
                            id="insight_scope_my_workspace"
                            defaultMessage="My workspace"
                          />
                          :
                        </strong>{" "}
                        <IDHFormattedMessage
                          id="ws_insight_scope_tooltip_my_workspace_text"
                          defaultMessage="Includes only publications from your workspace – use it to track your own brand and compare it with other dashboards."
                        />
                      </li>
                    </ul>
                  </>
                }
              />
            </CustomInputLabel>
            <PillSelector
              className="id-setup-pane__insight-scope-selector"
              options={[
                {
                  label: (
                    <IDHFormattedMessage
                      id="insight_scope_market_view"
                      defaultMessage="Market view"
                    />
                  ),
                  value: WorkspacePublicationsType.All,
                },
                {
                  label: (
                    <IDHFormattedMessage
                      id="insight_scope_my_workspace"
                      defaultMessage="My workspace"
                    />
                  ),
                  value: WorkspacePublicationsType.WorkspaceOnly,
                },
              ]}
              value={workspacePublicationsType}
              onChange={(v) => {
                setLocalSetupValue("workspacePublicationsType", v);
              }}
            />
          </ModalColumn>
        )}
      </div>

      <PaneFooter>
        <Button
          size="large"
          variant="white-with-light-grey-border"
          onClick={clear}
          data-qa="clear"
        >
          <EraserIcon />
          <IDHFormattedMessage id="ws_clear" defaultMessage="Clear" />
        </Button>

        <Button
          size="large"
          variant="blue"
          onClick={handleSubmit}
          disabled={loading || setupConfigLoading || dashboardLoading}
          data-qa="apply"
        >
          {setupMode === SetupModes.Create ? (
            <IDHFormattedMessage
              id="ws_create_dashboard"
              defaultMessage="Create dashboard"
            />
          ) : (
            <IDHFormattedMessage
              id="ws_apply_filters"
              defaultMessage="Apply Filters"
            />
          )}
        </Button>
      </PaneFooter>

      {showSaveModal && (
        <SaveDashboardModal onClose={() => setShowSaveModal(false)} />
      )}
    </Pane>
  );
};

export default InsightsDashboardSetupPane;
